import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { safeJsonParse } from '../utils/jsonUtils'

import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'

import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined'
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined'
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'

import axios from '../api/axios'
import toast from 'react-hot-toast'
import socket from '../socket'


const STALE_ACK_KEY = 'staleOrdersAcknowledgedCount'
const STALE_SESSION_KEY = 'staleDialogShown'


function DashboardCard({
   title,
   value,
   icon,
   actionText,
   onClick,
   className = '',
}) {
   return (
      <Paper
         onClick={onClick}
         className={className}
         elevation={0}
         sx={{
            position: 'relative',
            minWidth: 0,
            minHeight: 168,
            p: 2.5,

            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',

            cursor: 'pointer',

            backgroundColor: '#FFFFFF',

            border: '1px solid #E4E7EC',
            borderRadius: '12px',

            boxShadow:
               '0 1px 2px rgba(16, 24, 40, 0.04)',

            transition:
               'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',

            '&:hover': {
               transform: 'translateY(-2px)',
               borderColor: '#C9D6E3',
               boxShadow:
                  '0 8px 20px rgba(16, 24, 40, 0.08)',
            },
         }}
      >
         <Box
            sx={{
               display: 'flex',
               justifyContent: 'space-between',
               alignItems: 'flex-start',
               gap: 2,
            }}
         >
            <Box sx={{ minWidth: 0 }}>
               <Typography
                  sx={{
                     color: 'text.secondary',
                     fontSize: '14px',
                     fontWeight: 600,
                     lineHeight: 1.4,
                  }}
               >
                  {title}
               </Typography>

               <Typography
                  sx={{
                     mt: 0.7,
                     color: 'text.primary',
                     fontSize: {
                        xs: '30px',
                        md: '34px',
                     },
                     fontWeight: 700,
                     lineHeight: 1.1,
                     letterSpacing: '-0.8px',
                  }}
               >
                  {value}
               </Typography>
            </Box>

            <Box
               sx={{
                  width: 44,
                  height: 44,
                  flexShrink: 0,

                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',

                  borderRadius: '10px',

                  color: 'primary.main',
                  backgroundColor:
                     'rgba(22, 58, 95, 0.08)',

                  '& svg': {
                     fontSize: 24,
                  },
               }}
            >
               {icon}
            </Box>
         </Box>

         <Box
            sx={{
               mt: 2,
               display: 'flex',
               alignItems: 'center',
               gap: 0.5,

               color: 'primary.main',
            }}
         >
            <Typography
               sx={{
                  fontSize: '13px',
                  fontWeight: 600,
               }}
            >
               {actionText}
            </Typography>

            <ArrowForwardRoundedIcon
               sx={{ fontSize: 17 }}
            />
         </Box>
      </Paper>
   )
}


export default function AdminDashboard() {
   const [account, setAccount] = useState(null)

   const navigate = useNavigate()

   const [userCount, setUserCount] = useState(0)
   const [serviceCount, setServiceCount] = useState(0)
   const [requestCount, setRequestCount] = useState(0)
   const [unreadCount, setUnreadCount] = useState(0)

   const [supportUnreadCount, setSupportUnreadCount] =
      useState(0)

   const [archiveUnreadCount, setArchiveUnreadCount] =
      useState(0)

   const [requestAlert, setRequestAlert] =
      useState(true)

   const [staleDialog, setStaleDialog] =
      useState(false)

   const [staleCount, setStaleCount] =
      useState(0)

   const [staleOrderIds, setStaleOrderIds] =
      useState([])


   const getDashBoardData = async (id) => {
      try {
         await axios
            .post('getDashBoardData', { id })
            .then((result) => {
               setUserCount(
                  result.data.userCount
               )

               setServiceCount(
                  result.data.serviceCount
               )

               setRequestCount(
                  result.data.requestCount
               )

               setRequestAlert(
                  result.data.requestAlert
               )

               setUnreadCount(
                  result.data.supportUnreadCount +
                     result.data.archiveUnreadCount
               )

               setSupportUnreadCount(
                  result.data.supportUnreadCount
               )

               setArchiveUnreadCount(
                  result.data.archiveUnreadCount
               )
            })
      } catch (error) {
         if (process.env.REACT_APP_MODE) {
            console.log(error)
         }
      }
   }


   const checkStaleOrders = async () => {
      if (
         sessionStorage.getItem(
            STALE_SESSION_KEY
         )
      ) {
         return
      }

      try {
         const result = await axios.post(
            'getStaleOrders'
         )

         if (
            result.data.status &&
            result.data.count > 0
         ) {
            const acked = parseInt(
               localStorage.getItem(
                  STALE_ACK_KEY
               ) || '0',
               10
            )

            if (
               result.data.count > acked
            ) {
               setStaleCount(
                  result.data.count
               )

               setStaleOrderIds(
                  result.data.orderIds || []
               )

               setStaleDialog(true)

               sessionStorage.setItem(
                  STALE_SESSION_KEY,
                  '1'
               )
            }
         }
      } catch (error) {
         if (process.env.REACT_APP_MODE) {
            console.log(error)
         }
      }
   }


   const handleStaleOk = () => {
      localStorage.setItem(
         STALE_ACK_KEY,
         String(staleCount)
      )

      setStaleDialog(false)
   }


   const handleStaleCloseAll = async () => {
      try {
         await axios.post(
            'closeStaleOrders'
         )

         toast.success(
            `${staleCount} orders closed`
         )
      } catch (error) {
         if (process.env.REACT_APP_MODE) {
            console.log(error)
         }
      }

      setStaleDialog(false)
   }


   const handleStaleRemind = () => {
      setStaleDialog(false)
   }


   const setRequestStatus = async () => {
      navigate('/admin_upload')

      try {
         await axios
            .post('setRequestStatus')
            .then((result) => {
               if (!result.data.status) {
                  toast.error(
                     result.data.data
                  )
               }
            })
      } catch (error) {
         if (process.env.REACT_APP_MODE) {
            console.log(error)
         }
      }
   }


   useEffect(() => {
      const user =
         localStorage.getItem('user')

      if (user) {
         const parsed =
            safeJsonParse(user)

         setAccount(parsed)
      }
   }, [])


   useEffect(() => {
      if (!account) return

      getDashBoardData(account?._id)
      checkStaleOrders()

      const handleUnreadMessage = async () => {
         setUnreadCount(
            (prev) => prev + 1
         )
      }

      const handleRequestMessage = async () => {
         setRequestCount(
            (prev) => prev + 1
         )
      }

      socket.on(
         account?._id,
         handleUnreadMessage
      )

      socket.on(
         'request' + account?._id,
         handleRequestMessage
      )

      return () => {
         socket.off(
            account?._id,
            handleUnreadMessage
         )

         socket.off(
            'request' + account?._id,
            handleRequestMessage
         )
      }
   }, [account])


   return (
      <Box
         sx={{
            width: '100%',
            minWidth: 0,

            minHeight: 'calc(100vh - 60px)',

            backgroundColor:
               'background.default',

            px: {
               xs: 2,
               sm: 2.5,
               md: 3,
               xl: 4,
            },

            py: {
               xs: 2,
               md: 3,
            },
         }}
      >
         <Dialog
            open={staleDialog}
            onClose={handleStaleRemind}
         >
            <DialogTitle>
               In-progress orders
            </DialogTitle>

            <DialogContent>
               <DialogContentText>
                  {staleCount} order
                  {staleCount !== 1
                     ? 's have'
                     : ' has'}{' '}
                  been in-progress for over
                  2 weeks.
               </DialogContentText>
            </DialogContent>

            <DialogActions>
               <Button
                  onClick={
                     handleStaleRemind
                  }
               >
                  Remind me next login
               </Button>

               <Button
                  onClick={handleStaleOk}
               >
                  OK
               </Button>

               <Button
                  onClick={() => {
                     setStaleDialog(false)

                     navigate(
                        '/admin_upload',
                        {
                           state: {
                              staleOrderIds,
                           },
                        }
                     )
                  }}
               >
                  View orders
               </Button>

               <Button
                  onClick={
                     handleStaleCloseAll
                  }
                  variant="contained"
                  color="warning"
               >
                  Close all
               </Button>
            </DialogActions>
         </Dialog>


         <Box
            sx={{
               width: '100%',
               maxWidth: '1600px',
               mx: 'auto',
            }}
         >
            <Box
               sx={{
                  mb: {
                     xs: 2,
                     md: 2.5,
                  },
               }}
            >
               <Typography
                  sx={{
                     color: 'text.primary',
                     fontSize: {
                        xs: '20px',
                        md: '22px',
                     },
                     fontWeight: 600,
                     letterSpacing: '-0.3px',
                  }}
               >
                  Overview
               </Typography>

               <Typography
                  sx={{
                     mt: 0.4,
                     color: 'text.secondary',
                     fontSize: '14px',
                  }}
               >
                  Current system activity
                  and account overview.
               </Typography>
            </Box>


            <Box
               sx={{
                  width: '100%',

                  display: 'grid',

                  gridTemplateColumns: {
                     xs: 'minmax(0, 1fr)',
                     sm:
                        'repeat(2, minmax(0, 1fr))',
                     xl:
                        'repeat(4, minmax(0, 1fr))',
                  },

                  gap: {
                     xs: 2,
                     md: 2.5,
                  },

                  alignItems: 'stretch',
               }}
            >
               <DashboardCard
                  title="Total Users"
                  value={userCount}
                  icon={
                     <PeopleAltOutlinedIcon />
                  }
                  actionText="View users"
                  onClick={() =>
                     navigate('/admin_user')
                  }
               />


               <DashboardCard
                  title="Active Services"
                  value={serviceCount}
                  icon={
                     <BusinessCenterOutlinedIcon />
                  }
                  actionText="View services"
                  onClick={() =>
                     navigate(
                        '/admin_service'
                     )
                  }
               />


               <DashboardCard
                  title="Total Requests"
                  value={requestCount}
                  icon={
                     <CloudUploadOutlinedIcon />
                  }
                  actionText="View requests"
                  onClick={
                     setRequestStatus
                  }
                  className={
                     requestAlert
                        ? ''
                        : 'shaking-animation'
                  }
               />


               <DashboardCard
                  title="Unread Chat"
                  value={unreadCount}
                  icon={
                     <ForumOutlinedIcon />
                  }
                  actionText="Open messages"
                  onClick={() =>
                     navigate(
                        supportUnreadCount >=
                           archiveUnreadCount
                           ? '/admin_support'
                           : '/admin_archive'
                     )
                  }
                  className={
                     unreadCount > 0
                        ? 'shaking-animation'
                        : ''
                  }
               />
            </Box>
         </Box>
      </Box>
   )
}